import React, { Component, PropTypes } from 'react'
import moment from 'moment'
import { detectAndShowErrors, getProperty } from './utils'
import { ENDPOINT } from '../constants/APIEndpoints'
import classnames from 'classnames'
import {
    formHandler,
    TextInput,
    Select,
    RadioInput,
    Button,
    Label,
    SubHelp,
} from './ui'
import Header from './header'
import { storage } from '../utils'

class ICA extends Component {
    static propTypes = {
        account: PropTypes.shape({
            user: PropTypes.shape({
                contract: PropTypes.shape({
                    uuid: PropTypes.string.isRequired,
                }),
                crew: PropTypes.shape({
                    uuid: PropTypes.string.isRequired,
                }).isRequired,
                first_name: PropTypes.string,
                last_name: PropTypes.string,
                email: PropTypes.string,
            }).isRequired,
        }).isRequired,
        actions: PropTypes.shape({
            submitICA: PropTypes.func.isRequired,
        }).isRequired,
        appState: PropTypes.shape({
            loading: PropTypes.bool.isRequired,
        }).isRequired,
        docs: PropTypes.object.isRequired,
        docsActions: PropTypes.shape({
            loadDoc: PropTypes.func.isRequired,
        }).isRequired,
        ica: PropTypes.shape({
            errors: PropTypes.object,
        }).isRequired,
        location: PropTypes.shape({
            cities: PropTypes.array,
            countries: PropTypes.array,
            regions: PropTypes.array,
        }).isRequired,
        locationActions: PropTypes.shape({
            clearCities: PropTypes.func.isRequired,
            loadCities: PropTypes.func.isRequired,
            loadCountries: PropTypes.func.isRequired,
            loadRegions: PropTypes.func.isRequired,
        }).isRequired,
        personal: PropTypes.shape({
            data: PropTypes.shape({
                country: PropTypes.string,
            }),
        }).isRequired,
        personalActions: PropTypes.shape({
            loadPersonal: PropTypes.func.isRequired,
        }).isRequired,
    }

    constructor(props) {
        super(props)

        const today = moment()

        const data = {
            agreement_date: today,
            month: today.format('MM'),
            day: today.format('DD'),
            year: today.format('YYYY'),
            hour: parseInt(today.format('HH'), 10),
            minute: parseInt(today.format('mm'), 10),
        }

        if (this.props.account.user) {
            data.first_name = this.props.account.user.first_name
            data.last_name = this.props.account.user.last_name
            data.email = this.props.account.user.email
        }

        if (this.props.personal.data) {
            data.country = this.props.personal.data.country
        }

        this.state = {
            submitting: false,
            data,
        }

        this.updateValue = formHandler.updateValue.bind(this)

        // Tracks if the signature has started
        this._tool = {
            started: false,
        }
    }

    componentDidMount() {
        if (! this.contractSigned()) {
            if (this.props.personal.data) {
                if (this.props.personal.data.country && ! this.props.location.regions) {
                    this.props.locationActions.loadRegions(this.props.personal.data.country)
                }
            } else {
                this.props.personalActions.loadPersonal(this.props.account.user.crew.uuid)
            }

            if (! this.props.location.countries) {
                this.props.locationActions.loadCountries()
            }

            // load in the 'dynamic' ICA copy, but only if we're going to show the form
            if (! ('ica' in this.props.docs)) {
                this.props.docsActions.loadDoc('ica')
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        // If the next props has a personal.data.country that differs from the current
        // props.personal.data.country, set the country in state (for pre-filling of the form)
        if (nextProps.personal && nextProps.personal.data && ! (
            this.props.personal && this.props.personal.data &&
            nextProps.personal.data.country === this.props.personal.data.country
        )) {
            this.setState({
                data: {
                    ...this.state.data,
                    country: nextProps.personal.data.country,
                },
            })

            // Also load in the regions for this country
            if (nextProps.personal.data.country && ! this.props.location.regions) {
                this.props.locationActions.loadRegions(nextProps.personal.data.country)
            }
        }

        const newErrors = detectAndShowErrors(this.props, nextProps, 'ica.errors')

        if (newErrors) {
            this.setState({ submitting: false })
        }
    }

    submitICA = () => {
        this.setState({ submitting: true })
        this.props.actions.submitICA(this.state.data)
    }

    contractSigned() {
        if (! this.props.account.user) {
            return false
        }

        if (this.props.account.user.contract) {
            return true
        }

        return false
    }

    storeSignatureReference = (canvas) => {
        if (canvas) {
            // Register the canvas DOM node against the class
            canvas.addEventListener('mousedown', this.handleCanvasEvent)
            canvas.addEventListener('mousemove', this.handleCanvasEvent)
            canvas.addEventListener('mouseup', this.handleCanvasEvent)

            // Register the canvas context against the class
            this.canvasContext = canvas.getContext('2d')
            this.canvasContext.strokeStyle = '#000000'
            this.canvasContext.fillStyle = '#000000'
            this.canvasContext.lineWidth = 2
        } else {
            // Register the canvas DOM node against the class
            if (this.canvas) {
                this.canvas.removeEventListener('mousedown', this.handleCanvasEvent)
                this.canvas.removeEventListener('mousemove', this.handleCanvasEvent)
                this.canvas.removeEventListener('mouseup', this.handleCanvasEvent)
            }

            // Register the canvas context against the class
            this.canvasContext = null
        }

        this.canvas = canvas
    }

    hourOptions() {
        const options = []

        let i = 0

        // eslint-disable-next-line no-magic-numbers
        while (i < 24) {
            options.push({
                'value': i,
                'label': i.toString(),
            })

            i++
        }
        return options
    }

    minuteOptions() {
        const options = []

        let i = 0

        // eslint-disable-next-line no-magic-numbers
        while (i < 60) {
            options.push({
                'value': i,
                'label': i.toString(),
            })

            i++
        }
        return options
    }

    contractorTypeOptions() {
        return [
            {
                'value': 'INDIVIDUAL',
                'label': 'Individual',
            },
            {
                'value': 'LOAN-OUT-COMPANY',
                'label': 'Loan-out Company',
            },
        ]
    }

    updateDate = (data) => {
        for (const key in data) {
            this.updateDateSingle(key, data[key])
        }
    }

    updateDateSingle(key, value) {
        let {
            agreement_date,
            day,
            month,
            year,
            hour,
            minute,
        } = this.state.data

        switch (key) {
            case 'year':
                year = value
                break
            case 'month':
                month = value
                break
            case 'day':
                day = value
                break
            case 'hour':
                hour = value
                break
            case 'minute':
                minute = value
                break
        }

        const date_arr = {
            year: parseInt(year, 10),
            month: parseInt(month, 10) - 1,
            day: parseInt(day, 10),
            hour: parseInt(hour, 10),
            minute: parseInt(minute, 10),
        }

        agreement_date = moment(date_arr)

        const newState = {
            data: {
                ...this.state.data,
                agreement_date,
            },
        }

        this.setState(newState, () => this.updateValue({ [key]: value }))
    }

    /*
     * Canvas handling
     */

    getPointerPosition = (ev) => {
        // Get client position
        const client_x = ev.clientX
        const client_y = ev.clientY

        // Get the canvas position
        const canvas_rect = this.canvas.getBoundingClientRect()
        const canvas_x = canvas_rect.left
        const canvas_y = canvas_rect.top

        return {
            x: client_x - canvas_x,
            y: client_y - canvas_y,
        }
    }

    mousedown = (ev) => {
        const position = this.getPointerPosition(ev)

        this._tool.started = true

        this.canvasContext.beginPath()
        this.canvasContext.moveTo(position.x, position.y)
    }

    mousemove = (ev) => {
        if (this._tool.started) {
            const position = this.getPointerPosition(ev)

            this.canvasContext.lineTo(position.x, position.y)
            this.canvasContext.stroke()
        }
    }

    mouseup = (ev) => {
        if (this._tool.started) {
            this.mousemove(ev)

            this._tool.started = false

            this.updateValue({ signature: this.canvas.toDataURL() })
        }
    }

    clearCanvas = () => {
        this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height)

        this.updateValue({ signature: null })
    }

    handleCanvasEvent = (ev) => {
        // Firefox
        if (ev.layerX || ev.layerX === 0) {
            ev._x = ev.layerX
            ev._y = ev.layerY
        } else if (ev.offsetX || ev.offsetX === 0) {
            // Opera
            ev._x = ev.offsetX
            ev._y = ev.offsetY
        }

        // Call the event handler of the tool
        switch (ev.type) {
            case 'mousemove':
                this.mousemove(ev)
                break
            case 'mousedown':
                this.mousedown(ev)
                break
            case 'mouseup':
                this.mouseup(ev)
                break
        }
    }

    countryDialCodeOptions() {
        if (! this.props.location.countries) {
            return []
        }

        return this.props.location.countries.map((item) => {
            let label = item.label

            if (item.dial_code) {
                label += ` (${ item.dial_code })`
            }

            return {
                value: item.value,
                label,
            }
        })
    }

    countryIsUS(country) {
        if (! this.props.location.countries) {
            return false
        }

        return Boolean(this.props.location.countries.find(
            (item) => item.value === country && item.code === 'US'
        ))
    }

    onSelectCountry = (data) => {
        const country = data.country

        if (this.countryIsUS(country)) {
            this.props.locationActions.loadRegions(country)
        }

        // Update the internal form state - set country and unset region and city
        this.updateValue({
            country,
            region: null,
            city: null,
        })
    }

    onSelectRegion = (data) => {
        // Update the internal form state - set region and unset city
        this.updateValue({
            region: data.region,
            city: null,
        })

        this.props.locationActions.clearCities()
    }

    onSearchCity = (value) => {
        this.props.locationActions.loadCities(
            this.state.data.country,
            this.state.data.region,
            value
        )
    }

    render() {
        if (this.state.submitting) {
            return (
                <div>
                    <Header title="Independent Contractor Agreement" />

                    <div className="content">
                        <div className="content__inner">
                            <p style={{ fontSize: 60, textAlign: 'center' }}>
                                <span className="fa fa-spinner fa-pulse" />
                            </p>
                            <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
                                Please wait while we process your contract.
                                This may take up to 2 minutes.
                                DO NOT PRESS ANY BUTTONS OR REFRESH THE PAGE.
                            </p>
                        </div>
                    </div>
                </div>
            )
        }

        // If the user has already complete their contract, show a success page with a link to the ICA PDF
        if (this.contractSigned()) {
            const contractUUID = this.props.account.user.contract.uuid
            const authToken = storage.getItem('authToken')

            return (
                <div>
                    <Header title="Independent Contractor Agreement" />

                    <div className="content">
                        <div className="content__inner">
                            <p>Your Independent Contractor Agreement is already signed.</p>

                            <a
                                className="btn btn--primary"
                                href={ `${ ENDPOINT }assets/contract/download/${ contractUUID }?token=${ authToken }` }
                            >
                                Download ICA
                            </a>
                        </div>
                    </div>
                </div>
            )
        }

        const errors = this.props.ica.errors

        const canvasStyle = {
            backgroundColor: '#ffffff',
        }

        const signatureClasses = classnames({
            'signature': true,
            'signature--error': Boolean(errors.signature),
        })

        let signatureError = null

        if (errors.signature) {
            signatureError = <div className="alert alert--error">{ errors.signature }</div>
        }

        return (
            <div>
                <Header title="Independent Contractor Agreement" />

                <div className="content">
                    <div className="content__inner">
                        <div
                            style={{
                                background: '#f5f5f5',
                                marginTop: '-0.8em',
                                paddingTop: '0.8em',
                                marginLeft: '-2.1333em',
                                paddingLeft: '2.1333em',
                                marginRight: '-2.1333em',
                                paddingRight: '2.1333em',
                                paddingBottom: '0.8em',
                                marginBottom: '1.6em',
                            }}
                        >
                            <h4 className="align-center" style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                                Independent Contractor Agreement
                            </h4>
                            <p style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                                Before we can contract you for jobs, you must sign our Independent Contractor
                                Agreement (ICA). The ICA establishes a legal framework between Man With A Cam,
                                Inc. and its vendors to protect both parties from unlawful behavior and
                                unnecessary litigation in case a dispute occurs. Please review it carefully and
                                sign it below to continue our registration process. If you have any questions about
                                our ica, please  email us at
                                { ' ' }
                                <a href="mailto:support@hayden5.com">support@hayden5.com</a>
                                { ' ' }
                                and a team member will respond as soon as possible.
                            </p>
                        </div>

                        <div dangerouslySetInnerHTML={{ __html: getProperty(this.props.docs, 'ica.body', '') }} />

                        <div className="form">
                            <div className="form__fieldset">
                                <div className="form__fieldset-inner">
                                    <div className="form__legend">DATE OF AGREEMENT</div>
                                    <div className="grid">
                                        <div className="grid__span-2">
                                            <TextInput
                                                error={ errors.month }
                                                label="Month"
                                                name="month"
                                                type="text"
                                                updateValue={ this.updateDate }
                                                value={ this.state.data.month }
                                            />
                                        </div>
                                        <div className="grid__span-2">
                                            <TextInput
                                                error={ errors.day }
                                                label="Day"
                                                name="day"
                                                type="text"
                                                updateValue={ this.updateDate }
                                                value={ this.state.data.day }
                                            />
                                        </div>
                                        <div className="grid__span-2">
                                            <TextInput
                                                error={ errors.year }
                                                label="Year"
                                                name="year"
                                                type="text"
                                                updateValue={ this.updateDate }
                                                value={ this.state.data.year }
                                            />
                                        </div>
                                        <div className="grid__span-2">
                                            <Select
                                                error={ errors.hour }
                                                label="Hour"
                                                name="hour"
                                                options={ this.hourOptions() }
                                                updateValue={ this.updateDate }
                                                value={ this.state.data.hour }
                                            />
                                        </div>
                                        <div className="grid__span-2">
                                            <Select
                                                error={ errors.minute }
                                                label="Minute"
                                                name="minute"
                                                options={ this.minuteOptions() }
                                                updateValue={ this.updateDate }
                                                value={ this.state.data.minute }
                                            />
                                        </div>
                                    </div>
                                    <SubHelp
                                        help={ this.state.data.agreement_date.format('dddd, MMMM Do YYYY, HH:mm') }
                                    />
                                </div>
                            </div>
                            <div className="form__fieldset">
                                <div className="form__fieldset-inner">
                                    <div className="form__legend">CONTRACTOR INFORMATION</div>
                                    <div className="grid">
                                        <div className="grid__span-6">
                                            <RadioInput
                                                error={ errors.contractor_type }
                                                label="Contractor is the following"
                                                name="contractor_type"
                                                options={ this.contractorTypeOptions() }
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.contractor_type }
                                            />
                                        </div>
                                    </div>
                                    <div className="grid">
                                        <div className="grid__span-6">
                                            <TextInput
                                                error={ errors.full_legal_name }
                                                label="Full Legal Name of Contractor"
                                                name="full_legal_name"
                                                subHelp="If a loan-out company, input company name here."
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.full_legal_name }
                                            />
                                        </div>
                                    </div>
                                    <Label label="Name of Individual Signing on behalf of Contractor" />
                                    <div className="grid">
                                        <div className="grid__span-4">
                                            <TextInput
                                                error={ errors.first_name }
                                                name="first_name"
                                                placeholder="First name"
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.first_name }
                                            />
                                        </div>
                                        <div className="grid__span-5">
                                            <TextInput
                                                error={ errors.last_name }
                                                name="last_name"
                                                placeholder="Last name"
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.last_name }
                                            />
                                        </div>
                                    </div>
                                    <div className="grid">
                                        <div className="grid__span-6">
                                            <TextInput
                                                error={ errors.title }
                                                label="Title (e.g., President, CEO, CFO, VP)"
                                                name="title"
                                                subHelp={ 'If Contractor is an individual, write "Individual".' }
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.title }
                                            />
                                        </div>
                                    </div>
                                    <div className="grid">
                                        <div className="grid__span-4">
                                            <TextInput
                                                error={ errors.email }
                                                label="Contractor's E-mail"
                                                name="email"
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.email }
                                            />
                                        </div>
                                        <div className="grid__span-4">
                                            <Select
                                                error={ errors.phone_number_dial_code }
                                                label="Contractor's Phone Number"
                                                name="phone_number_dial_code"
                                                options={ this.countryDialCodeOptions() }
                                                placeholder="Select your country code"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.phone_number_dial_code }
                                            />
                                        </div>
                                        <div className="grid__span-4">
                                            <TextInput
                                                error={ errors.phone_number }
                                                label="&nbsp;"
                                                name="phone_number"
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.phone_number }
                                            />
                                        </div>
                                    </div>
                                    <Label label="Contractor's Address" />
                                    <div className="grid">
                                        <div className="grid__span-4">
                                            <Select
                                                error={ errors.country }
                                                name="country"
                                                options={ this.props.location.countries }
                                                placeholder="Select country"
                                                updateValue={ this.onSelectCountry }
                                                value={ this.state.data.country }
                                            />
                                        </div>
                                        { this.countryIsUS(this.state.data.country) &&
                                            <div className="grid__span-4">
                                                <Select
                                                    error={ errors.region }
                                                    name="region"
                                                    options={ this.props.location.regions }
                                                    placeholder="Select state / province"
                                                    updateValue={ this.onSelectRegion }
                                                    value={ this.state.data.region }
                                                />
                                            </div>
                                        }
                                    </div>
                                    <div className="grid">
                                        <div className="grid__span-4">
                                            <Select
                                                error={ errors.city }
                                                name="city"
                                                noOptionPlaceholder="Type in city name"
                                                options={ this.props.location.cities }
                                                placeholder="Select city"
                                                searchOptions={ this.onSearchCity }
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.city }
                                            />
                                        </div>
                                        <div className="grid__span-4">
                                            <TextInput
                                                error={ errors.zipcode }
                                                name="zipcode"
                                                placeholder="Zipcode"
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.zipcode }
                                            />
                                        </div>
                                    </div>
                                    <div className="grid">
                                        <div className="grid__span-6">
                                            <TextInput
                                                error={ errors.address_street_line_1 }
                                                name="address_street_line_1"
                                                placeholder="Street address (line 1)"
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.address_street_line_1 }
                                            />
                                        </div>
                                    </div>
                                    <div className="grid">
                                        <div className="grid__span-6">
                                            <TextInput
                                                error={ errors.address_street_line_2 }
                                                name="address_street_line_2"
                                                placeholder="Street address (line 2)"
                                                type="text"
                                                updateValue={ this.updateValue }
                                                value={ this.state.data.address_street_line_2 }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="form__fieldset">
                                <div className="form__fieldset-inner">
                                    <div className="form__legend">
                                        Contractor has reviewed and accepts this agreement:
                                    </div>

                                    <Label>
                                        Contractor's Signature -
                                        { ' ' }
                                        <strong>Please use your mouse to sign in the box below</strong>:
                                    </Label>

                                    { signatureError }
                                    <div className={ signatureClasses } id="signature_container">
                                        <canvas
                                            height="200px"
                                            ref={ this.storeSignatureReference }
                                            style={ canvasStyle }
                                            width="580px"
                                        />
                                    </div>

                                    <button className="btn btn--small float-right" onClick={ this.clearCanvas }>
                                        Clear signature
                                    </button>
                                    <SubHelp help="As Authorized Signatory for Contractor" />
                                </div>
                            </div>
                            <Button
                                loading={ this.props.appState.loading }
                                onClick={ this.submitICA }
                                status="create"
                            >
                                Submit contract
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default ICA



// WEBPACK FOOTER //
// ./app/components/ica.js